import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography,
  Card,
  Button,
  IconButton,
  Divider,
  DialogActions,
} from "@material-ui/core";
import productPageStyle from "features/products/styles";
import { useTranslation } from "react-i18next";
import { emptyArray } from "constants/defaultValues";
import { CheckBoxOutlined, Close, GetApp, MailOutline } from "@material-ui/icons";
import * as R from "ramda";
import * as V from "@totalsoft/pure-validations";
import ActionsDialog from "features/common/components/ActionsDialog";
import { productConstants } from "../constants";
import EmailAddressContent from "features/common/components/EmailAddressContent";
import { countryCodeConstants } from "features/common/constants";
import { getTenant } from "providers/TenantProvider";

const useStyles = makeStyles(productPageStyle);

function SendOrDownloadProduct({
  openViewDialogType,
  openViewDialog,
  setOpenViewDialog,
  quoteProducts,
  currencyCode,
  showAmountsWithVAT,
  onDownloadProducts,
  onSendProductsOnEmail,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { countryCode } = getTenant();
  const [open, setOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(emptyArray);

  const handleClose = useCallback(() => {
    setOpenViewDialog(false);
  }, [setOpenViewDialog]);

  const handleSelect = useCallback(
    (quoteProductId) => () => {
      const index = selectedProducts.indexOf(quoteProductId);
      const newSelectedProducts = [...selectedProducts];
      index === -1 ? newSelectedProducts.push(quoteProductId) : newSelectedProducts.splice(index, 1);
      setSelectedProducts(newSelectedProducts);
    },
    [selectedProducts]
  );

  const handleClick = useCallback(
    (event) => {
      const type = event.currentTarget.id;
      switch (type) {
        case "btnSendViaEmail":
          setOpen(true);
          break;
        case "btnDownload":
          onDownloadProducts(selectedProducts);
          setOpenViewDialog(false);
          setSelectedProducts(emptyArray);
          break;
      }
    },
    [selectedProducts, onDownloadProducts, setOpenViewDialog]
  );

  return (
    <>
      <Dialog scroll="paper" maxWidth="md" fullWidth={true} open={openViewDialog} onClose={handleClose}>
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4">
                {openViewDialogType === "product_sendViaEmail"
                  ? t("Product.EmailSelectedQuotes")
                  : t("Product.DownloadSelectedQuotes")}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {productConstants.productTypeIds
            .filter((p) =>
              R.contains(
                p,
                quoteProducts.map((qp) => qp.productTypeId)
              )
            )
            .map((productTypeId) => {
              const quoteProductsByProductType = quoteProducts.filter((p) => p.productTypeId === productTypeId);
              return (
                <Grid key={quoteProductsByProductType[0].productTypeName}>
                  <Grid item>
                    <Typography>{quoteProductsByProductType[0].productTypeName}</Typography>
                  </Grid>
                  {quoteProductsByProductType.map((product) => (
                    <Card id={"productCard"} key={product.quoteProductId} className={classes.card}>
                      <Grid container>
                        <Grid item xs={4} className={classes.productSection}>
                          <div className={classes.arrowTipItem}>
                            <Typography className={classes.productName}>{product.productName}</Typography>
                            {product.scheduleIsConfigurable &&
                              countryCode !== countryCodeConstants.china &&
                              countryCode !== countryCodeConstants.romania && (
                                <Typography className={classes.scheduleType}>
                                  {t("Product.SeasonalSchedule")}
                                </Typography>
                              )}
                            {!product.scheduleIsConfigurable && countryCode === countryCodeConstants.brazil && (
                              <Typography variant="body2" className={classes.scheduleType}>
                                {t("Product.InstallmentAmount")}
                              </Typography>
                            )}
                          </div>
                        </Grid>
                        <Grid
                          item
                          container
                          xs={3}
                          direction="column"
                          className={
                            product.residualValueVisible
                              ? classes.paymentInfoWithResidual
                              : classes.paymentInfoWithoutResidual
                          }
                        >
                          <Typography className={classes.minFont}>
                            {showAmountsWithVAT ? t("Product.DownPaymentWithVAT") : t("Product.DownPayment")}
                          </Typography>
                          {showAmountsWithVAT ? (
                            <Typography className={classes.reducedFont}>
                              {currencyCode} {t("DECIMAL2", { number: product.downPaymentAmountWithVAT })}
                            </Typography>
                          ) : (
                            <Typography className={classes.reducedFont}>
                              {currencyCode} {t("DECIMAL2", { number: product.downPaymentAmount })}
                            </Typography>
                          )}
                          <Typography className={classes.minFont}>{t("Product.Term")}</Typography>
                          <Typography className={classes.reducedFont}>{product.duration}</Typography>
                          {product.residualValueVisible && (
                            <>
                              <Typography className={classes.minFont}>
                                {showAmountsWithVAT ? t("Product.ResidualValueWithVAT") : t("Product.ResidualValue")}
                              </Typography>
                              {showAmountsWithVAT ? (
                                <Typography className={classes.reducedFont}>
                                  {currencyCode} {t("DECIMAL2", { number: product.residualValueAmountWithVAT })}
                                </Typography>
                              ) : (
                                <Typography className={classes.reducedFont}>
                                  {currencyCode} {t("DECIMAL2", { number: product.residualValueAmount })}
                                </Typography>
                              )}
                            </>
                          )}
                        </Grid>
                        <Grid
                          item
                          container
                          xs={3}
                          justifyContent="space-around"
                          direction="column"
                          className={classes.gradientLine}
                        >
                          <Grid item xs={6}>
                            <Typography className={classes.minFont}>{t("QuoteHistory.Downloaded")}</Typography>
                            <Typography className={classes.dateWidth}>
                              {product.downloadTimestamp
                                ? t("DATE_FORMAT", {
                                    date: { value: product.downloadTimestamp, format: "DD.MM.YYYY" },
                                  })
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography className={classes.minFont}>{t("QuoteHistory.EmailedToClient")}</Typography>
                            <Typography className={classes.dateWidth}>
                              {product.mailTimestamp
                                ? t("DATE_FORMAT", {
                                    date: { value: product.mailTimestamp, format: "DD.MM.YYYY" },
                                  })
                                : "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item container xs={2} justifyContent="space-around" alignItems="center">
                          {R.any((x) => x === product.quoteProductId, selectedProducts) ? (
                            <IconButton color="primary" onClick={handleSelect(product.quoteProductId)}>
                              <CheckBoxOutlined />
                            </IconButton>
                          ) : (
                            <Button
                              variant="outlined"
                              className={classes.reducedFont}
                              onClick={handleSelect(product.quoteProductId)}
                              disabled={!product.hasSchedule}
                            >
                              {t("General.Select")}
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Grid>
              );
            })}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              {openViewDialogType === "product_download" && <p>{t("Product.DownloadSelectedQuotes")}</p>}
            </Grid>
            <Grid item>
              {openViewDialogType === "product_sendViaEmail" ? (
                <Button
                  id="btnSendViaEmail"
                  variant="contained"
                  color="primary"
                  onClick={handleClick}
                  disabled={selectedProducts.length === 0}
                  endIcon={<MailOutline />}
                >
                  {t("Product.SendViaEMAIL")}
                </Button>
              ) : (
                <Button
                  id="btnDownload"
                  variant="contained"
                  color="primary"
                  onClick={handleClick}
                  disabled={selectedProducts.length === 0}
                  endIcon={<GetApp />}
                >
                  {t("Product.Download")}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <ActionsDialog
        open={open}
        handleClose={useCallback(() => {
          setSelectedProducts(emptyArray);
          setOpen(false);
        }, [])}
        title={t("Product.ProvideUsEmailAddress")}
        validator={V.shape({ email: [V.required, V.email] |> V.stopOnFirstFailure })}
        actions={[
          {
            caption: t("General.Confirm"),
            handle: (state, validate) => {
              if (!validate(state)) return;
              onSendProductsOnEmail(state.email, selectedProducts);
              setOpenViewDialog(false);
              return true;
            },
          },
        ]}
        Content={EmailAddressContent}
      />
    </>
  );
}

SendOrDownloadProduct.propTypes = {
  openViewDialogType: PropTypes.string.isRequired,
  openViewDialog: PropTypes.bool.isRequired,
  setOpenViewDialog: PropTypes.func.isRequired,
  quoteProducts: PropTypes.array.isRequired,
  currencyCode: PropTypes.string.isRequired,
  showAmountsWithVAT: PropTypes.bool.isRequired,
  onDownloadProducts: PropTypes.func.isRequired,
  onSendProductsOnEmail: PropTypes.func.isRequired,
};

export default SendOrDownloadProduct;
